import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Zap, Shield, Building2, Banknote, Camera, 
  RefreshCw, CheckCircle, XCircle, Clock, ArrowLeft,
  Play, Settings, Database
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

export default function AdminSimulation() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  
  // Simulation form states
  const [kycForm, setKycForm] = useState({ subject_type: 'tenant', subject_id: '', check_types: ['id', 'address', 'aml', 'credit'], result: 'passed' });
  const [listingForm, setListingForm] = useState({ property_id: '', portal_code: 'rightmove', result: 'published' });
  const [depositForm, setDepositForm] = useState({ tenancy_id: '', amount: 1200, result: 'confirmed' });
  const [inspectionForm, setInspectionForm] = useState({ inspection_case_id: '', stage: 'kit_ordered' });
  
  // Available entities for selection
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [inspectionCases, setInspectionCases] = useState([]);

  useEffect(() => {
    loadDashboard();
    loadEntities();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        setDashboard(await response.json());
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEntities = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${getToken()}` };
      
      const [usersRes, propertiesRes, tenanciesRes, inspectionsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users`, { headers }),
        fetch(`${API_URL}/api/admin/properties`, { headers }),
        fetch(`${API_URL}/api/admin/tenancies`, { headers }).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/inspections/cases/overdue`, { headers }).catch(() => ({ ok: false }))
      ]);
      
      if (usersRes.ok) setUsers((await usersRes.json()).users || []);
      if (propertiesRes.ok) setProperties((await propertiesRes.json()).properties || []);
      if (tenanciesRes.ok) setTenancies((await tenanciesRes.json()).tenancies || []);
      if (inspectionsRes.ok) {
        const data = await inspectionsRes.json();
        setInspectionCases(data.overdue_cases || []);
      }
    } catch (error) {
      console.error('Failed to load entities:', error);
    }
  };

  const simulateKYC = async () => {
    if (!kycForm.subject_id) {
      toast.error('Please select a user');
      return;
    }
    
    setSimulating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/simulate/kyc`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(kycForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Simulation failed');
      }
    } catch (error) {
      toast.error('Failed to simulate KYC');
    } finally {
      setSimulating(false);
    }
  };

  const simulateListing = async () => {
    if (!listingForm.property_id) {
      toast.error('Please select a property');
      return;
    }
    
    setSimulating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/simulate/listing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(listingForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Simulation failed');
      }
    } catch (error) {
      toast.error('Failed to simulate listing');
    } finally {
      setSimulating(false);
    }
  };

  const simulateDeposit = async () => {
    if (!depositForm.tenancy_id) {
      toast.error('Please enter a tenancy ID');
      return;
    }
    
    setSimulating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/simulate/deposit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(depositForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Simulation failed');
      }
    } catch (error) {
      toast.error('Failed to simulate deposit');
    } finally {
      setSimulating(false);
    }
  };

  const simulateInspection = async () => {
    if (!inspectionForm.inspection_case_id) {
      toast.error('Please enter an inspection case ID');
      return;
    }
    
    setSimulating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/simulate/inspection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inspectionForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Inspection simulation: ${data.status}`);
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Simulation failed');
      }
    } catch (error) {
      toast.error('Failed to simulate inspection');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin/console">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Zap className="w-8 h-8 text-yellow-500" />
              Integration Simulator
            </h1>
            <p className="text-gray-600">Test integration workflows with simulated API responses</p>
          </div>
        </div>
        <Button onClick={loadDashboard} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="font-medium">KYC/AML</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{dashboard.kyc.verified}</div>
            <div className="text-xs text-gray-500">verified | {dashboard.kyc.pending} pending | {dashboard.kyc.failed} failed</div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              <span className="font-medium">Listings</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{dashboard.listings.published}</div>
            <div className="text-xs text-gray-500">published | {dashboard.listings.failed} failed</div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="w-5 h-5 text-green-600" />
              <span className="font-medium">Deposits</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{dashboard.deposits.confirmed}</div>
            <div className="text-xs text-gray-500">confirmed | {dashboard.deposits.pending} pending</div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-5 h-5 text-orange-600" />
              <span className="font-medium">Inspections</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{dashboard.inspections.report_ready}</div>
            <div className="text-xs text-gray-500">reports ready | {dashboard.inspections.in_progress} in progress</div>
          </Card>
        </div>
      )}

      {/* Outbox/Inbox Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Integration Outbox</span>
            </div>
            <div className="flex gap-4 text-sm">
              <div><span className="font-bold text-yellow-600">{dashboard.outbox.pending}</span> pending</div>
              <div><span className="font-bold text-red-600">{dashboard.outbox.failed}</span> failed</div>
              <div><span className="font-bold text-green-600">{dashboard.outbox.done}</span> done</div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-purple-600" />
              <span className="font-medium">Integration Inbox</span>
            </div>
            <div className="flex gap-4 text-sm">
              <div><span className="font-bold text-yellow-600">{dashboard.inbox.unparsed}</span> unparsed</div>
              <div><span className="font-bold text-gray-600">{dashboard.inbox.total}</span> total</div>
            </div>
          </Card>
        </div>
      )}

      {/* Simulation Forms */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* KYC Simulation */}
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            KYC/AML Verification
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Subject Type</label>
              <select
                value={kycForm.subject_type}
                onChange={(e) => setKycForm({...kycForm, subject_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="tenant">Tenant</option>
                <option value="landlord">Landlord</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Select User</label>
              <select
                value={kycForm.subject_id}
                onChange={(e) => setKycForm({...kycForm, subject_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">-- Select User --</option>
                {users.filter(u => u.role === kycForm.subject_type).map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Result</label>
              <select
                value={kycForm.result}
                onChange={(e) => setKycForm({...kycForm, result: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="passed">✅ Passed</option>
                <option value="failed">❌ Failed</option>
                <option value="referred">⚠️ Referred (Manual Review)</option>
              </select>
            </div>
            
            <Button onClick={simulateKYC} disabled={simulating} className="w-full">
              <Play className="w-4 h-4 mr-2" />
              Simulate KYC Check
            </Button>
          </div>
        </Card>

        {/* Listing Simulation */}
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Portal Listing
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Select Property</label>
              <select
                value={listingForm.property_id}
                onChange={(e) => setListingForm({...listingForm, property_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">-- Select Property --</option>
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>
                    {prop.address?.line1}, {prop.address?.city}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Portal</label>
              <select
                value={listingForm.portal_code}
                onChange={(e) => setListingForm({...listingForm, portal_code: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="rightmove">Rightmove</option>
                <option value="zoopla">Zoopla</option>
                <option value="gumtree">Gumtree</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Result</label>
              <select
                value={listingForm.result}
                onChange={(e) => setListingForm({...listingForm, result: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="published">✅ Published</option>
                <option value="failed">❌ Failed</option>
              </select>
            </div>
            
            <Button onClick={simulateListing} disabled={simulating} className="w-full">
              <Play className="w-4 h-4 mr-2" />
              Simulate Listing
            </Button>
          </div>
        </Card>

        {/* Deposit Simulation */}
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-green-600" />
            Deposit Lodgement
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Tenancy ID</label>
              <input
                type="text"
                value={depositForm.tenancy_id}
                onChange={(e) => setDepositForm({...depositForm, tenancy_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter tenancy ID"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Amount (£)</label>
              <input
                type="number"
                value={depositForm.amount}
                onChange={(e) => setDepositForm({...depositForm, amount: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Result</label>
              <select
                value={depositForm.result}
                onChange={(e) => setDepositForm({...depositForm, result: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="confirmed">✅ Confirmed</option>
                <option value="failed">❌ Failed</option>
              </select>
            </div>
            
            <Button onClick={simulateDeposit} disabled={simulating} className="w-full">
              <Play className="w-4 h-4 mr-2" />
              Simulate Deposit
            </Button>
          </div>
        </Card>

        {/* Inspection Simulation */}
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-orange-600" />
            Inspection Workflow
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Inspection Case ID</label>
              <input
                type="text"
                value={inspectionForm.inspection_case_id}
                onChange={(e) => setInspectionForm({...inspectionForm, inspection_case_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter inspection case ID"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Stage to Simulate</label>
              <select
                value={inspectionForm.stage}
                onChange={(e) => setInspectionForm({...inspectionForm, stage: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="kit_ordered">📦 Kit Ordered</option>
                <option value="shipped">🚚 Shipped</option>
                <option value="delivered">📬 Delivered</option>
                <option value="returned">↩️ Returned</option>
                <option value="report_ready">📊 Report Ready</option>
              </select>
            </div>
            
            <Button onClick={simulateInspection} disabled={simulating} className="w-full">
              <Play className="w-4 h-4 mr-2" />
              Simulate Stage
            </Button>
          </div>
        </Card>
      </div>

      {/* Legend */}
      <Card className="p-4 mt-8 bg-yellow-50 border-yellow-200">
        <h4 className="font-bold text-yellow-800 mb-2">🧪 Simulation Mode</h4>
        <p className="text-sm text-yellow-700">
          These simulations create real database entries as if external providers had responded.
          Use this to test full end-to-end workflows without actual integrations.
          When real providers are connected, replace SIM_* handlers with actual API calls.
        </p>
      </Card>
    </div>
  );
}
